import { Link } from "react-router-dom";
import { useReveal } from "@/lib/useReveal";

const EMAIL = "Info.cleanwaterdrop@gmail.com";

const SECTIONS = [
  {
    title: "Overview",
    body: (
      <p>
        CLEAN Water Drop Association ("we", "our", "us") is a community-led
        organisation. This Privacy Policy explains what information we collect
        when you use our website, how we store it, and how we use it. We aim to
        keep this short, clear, and respectful — in line with the trauma-informed
        principles that guide our work.
      </p>
    ),
  },
  {
    title: "Information we collect",
    body: (
      <>
        <p>We only collect information that you voluntarily share with us through our forms:</p>
        <ul className="mt-4 space-y-2 list-disc pl-5">
          <li><strong>Contact form</strong> — your name, email address, optional subject, and the message you send us.</li>
          <li><strong>Volunteer form</strong> — your name, email, optional phone number, city, areas of interest, availability, and any message you choose to share.</li>
          <li><strong>Donation enquiry form</strong> — your name and email (unless you give anonymously), the amount you intend to give, the program you'd like to direct your gift to, frequency, and any note you include.</li>
          <li><strong>Newsletter subscription</strong> — your email address only.</li>
        </ul>
      </>
    ),
  },
  {
    title: "How we store this information",
    body: (
      <p>
        Information you share is stored securely in our database, accessible only
        to authorised members of the CLEAN Water Drop Association team. We never
        sell, rent, or trade your personal information.
      </p>
    ),
  },
  {
    title: "How we use this information",
    body: (
      <>
        <p>We use the information you share for the following purposes only:</p>
        <ul className="mt-4 space-y-2 list-disc pl-5">
          <li>To respond to your enquiry, message, or volunteer application.</li>
          <li>To follow up regarding a donation you have offered.</li>
          <li>To send occasional updates if you have subscribed to our newsletter.</li>
        </ul>
        <p className="mt-4">
          We do not use your information for marketing, advertising, profiling, or
          any automated decision-making.
        </p>
      </>
    ),
  },
  {
    title: "Confirmation emails",
    body: (
      <p>
        When you submit a contact, volunteer, or donation enquiry form, we may
        send you a single, simple confirmation email to acknowledge that your
        message has been received. These emails are transactional only — they
        contain no marketing or fundraising content.
      </p>
    ),
  },
  {
    title: "Your rights",
    body: (
      <p>
        You may ask us at any time to access, correct, or delete any information
        we hold about you, or to unsubscribe from any future communications.
        Please contact us at{" "}
        <a href={`mailto:${EMAIL}`} className="text-[#2E8B8B] underline">
          {EMAIL}
        </a>{" "}
        and we will respond as soon as we can.
      </p>
    ),
  },
  {
    title: "Changes to this policy",
    body: (
      <p>
        We may occasionally update this Privacy Policy to reflect changes to our
        services or to legal requirements. Any updates will be published on this
        page.
      </p>
    ),
  },
  {
    title: "Contact",
    body: (
      <p>
        Questions about this policy can be sent to{" "}
        <a href={`mailto:${EMAIL}`} className="text-[#2E8B8B] underline">{EMAIL}</a>.
      </p>
    ),
  },
];

export default function Privacy() {
  useReveal();
  return (
    <div data-testid="privacy-page">
      <section className="ripple-bg">
        <div className="container-app pt-24 sm:pt-32 pb-16">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 reveal">
              <p className="eyebrow">Privacy Policy</p>
              <h1 className="font-heading mt-6 text-[44px] sm:text-[64px] lg:text-[72px] leading-[0.98] tracking-[-0.02em] font-semibold text-[#0A2540]">
                How we handle your information.
              </h1>
            </div>
            <div className="lg:col-span-4 reveal">
              <p className="text-lg text-slate-600 leading-relaxed">
                Short, clear, and respectful — in line with the trauma-informed
                principles that guide our work.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-app pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <aside className="lg:col-span-4 reveal lg:sticky lg:top-28">
            <p className="eyebrow">On this page</p>
            <ul className="mt-5 space-y-3">
              {SECTIONS.map((s, i) => (
                <li key={s.title}>
                  <a
                    href={`#section-${i}`}
                    className="text-sm text-slate-600 hover:text-[#0A2540] transition-colors"
                  >
                    {String(i + 1).padStart(2, "0")} — {s.title}
                  </a>
                </li>
              ))}
            </ul>
            <Link to="/contact" className="btn-outline mt-8 inline-flex" data-testid="privacy-contact-cta">
              Have a question?
            </Link>
          </aside>

          <div className="lg:col-span-8 space-y-14 reveal">
            {SECTIONS.map((s, i) => (
              <section key={s.title} id={`section-${i}`} data-testid={`privacy-section-${i}`}>
                <p className="font-heading text-sm text-slate-400 tracking-widest">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h2 className="font-heading mt-3 text-2xl sm:text-3xl text-[#0A2540] leading-snug">
                  {s.title}
                </h2>
                <div className="mt-5 text-[15px] text-slate-600 leading-[1.75] space-y-3">
                  {s.body}
                </div>
              </section>
            ))}
            <p className="pt-6 border-t border-slate-200 text-xs text-slate-400">
              Last updated: {new Date().toLocaleDateString("en-AU", { month: "long", year: "numeric" })}.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
